import axios from 'axios';
import {
  ASSIGN_MANUAL_VERIFICATION_FORM_API,
  GET_LINKED_MANUAL_VERIFICATION_FORMS_API,
  UNASSIGN_MANUAL_VERIFICATION_FORM_API,
  get_request_config,
} from '@/utils/system_constants/constant.js';
import {
  build_mapping_route_query,
  build_onboarding_links,
  get_assigned_onboarding_ids,
  get_first_assigned_onboarding_form_value,
  get_response_items,
  merge_assigned_onboarding_ids,
  normalize_manual_link,
  replace_assigned_onboarding_ids,
} from './manual_verification.helpers';
import { get_manual_verification_error_message } from './manual_verification_forms';

function update_form_assignment(forms = [], get_form_id, manual_verification_form_id, updater) {
  const target_form_id = String(manual_verification_form_id);
  const updated_forms = (forms || []).map((form) => {
    if (String(get_form_id(form)) !== target_form_id) {
      return form;
    }
    return updater(form);
  });
  return updated_forms;
}

export default {
  data: () => ({
    selected_assign_verification_form: null,
    loading_linked_onboarding_forms: false,
    linking_onboarding_form_id: null,
    linking_onboarding_form_action: null,
    onboarding_links: {},
    linked_onboarding_forms: [],
  }),
  computed: {
    assign_modal_forms() {
      const forms = Array.isArray(this.onboarding_forms) ? this.onboarding_forms : [];
      const selected_builder_form_id = this.first_non_null(
        this.selected_assign_verification_form?.form_id,
        this.selected_assign_verification_form?.survey_form_id,
        this.selected_assign_verification_form?.survey_form,
      );
      const filtered_forms = forms.filter((form) => {
        if (!selected_builder_form_id) {
          return true;
        }
        return String(form?.id) !== String(selected_builder_form_id)
          && String(form?.form_id) !== String(selected_builder_form_id);
      });
      return filtered_forms;
    },
  },
  methods: {
    async fetch_linked_onboarding_forms(manual_verification_form_id, only_active = true) {
      this.loading_linked_onboarding_forms = true;
      try {
        const response = await axios.get(
          GET_LINKED_MANUAL_VERIFICATION_FORMS_API,
          get_request_config({ params: { manual_verification_form_id, only_active } }),
        );
        const parsed = this.parse_response_data(response);
        const items = get_response_items(parsed, 'data', 'results');
        const links = items.map(normalize_manual_link).filter(Boolean);
        this.linked_onboarding_forms = links;
        this.onboarding_links = build_onboarding_links(links);
        this.update_form_assignment_state_from_links(manual_verification_form_id, links);
        return links;
      } catch (error) {
        this.linked_onboarding_forms = [];
        this.onboarding_links = {};
        this.show_error(get_manual_verification_error_message(error, 'Failed to load assigned onboarding forms.'));
        return [];
      } finally {
        this.loading_linked_onboarding_forms = false;
      }
    },

    async open_assign_modal(form) {
      this.selected_assign_verification_form = form || null;
      this.onboarding_links = {};
      this.linked_onboarding_forms = [];
      const verification_form_id = this.get_manual_verification_form_id(this.selected_assign_verification_form);
      if (!verification_form_id) {
        this.show_error('Manual verification form id not found for linking.');
        return;
      }
      if (!this.onboarding_forms.length) {
        await this.fetch_onboarding_forms();
      }
      await this.fetch_linked_onboarding_forms(verification_form_id, true);
    },

    create_updated_links(onboarding_form_id, value) {
      const next = { ...(this.onboarding_links || {}) };
      next[String(onboarding_form_id)] = value;
      return next;
    },

    set_onboarding_link_state(onboarding_form_id, value) {
      this.onboarding_links = this.create_updated_links(onboarding_form_id, value);
    },

    update_form_assignment_state(manual_verification_form_id, onboarding_form_id, assigned) {
      this.verification_forms = update_form_assignment(
        this.verification_forms,
        (form) => this.get_manual_verification_form_id(form),
        manual_verification_form_id,
        (form) => ({
          ...form,
          assigned_onboarding_form_ids: merge_assigned_onboarding_ids(
            form?.assigned_onboarding_form_ids,
            onboarding_form_id,
            assigned,
          ),
        }),
      );
      if (this.selected_assign_verification_form
        && String(this.get_manual_verification_form_id(this.selected_assign_verification_form)) === String(manual_verification_form_id)) {
        this.selected_assign_verification_form = {
          ...this.selected_assign_verification_form,
          assigned_onboarding_form_ids: merge_assigned_onboarding_ids(
            this.selected_assign_verification_form.assigned_onboarding_form_ids,
            onboarding_form_id,
            assigned,
          ),
        };
      }
    },

    update_form_assignment_state_from_links(manual_verification_form_id, links) {
      const assigned_ids = get_assigned_onboarding_ids(links);
      this.verification_forms = update_form_assignment(
        this.verification_forms,
        (form) => this.get_manual_verification_form_id(form),
        manual_verification_form_id,
        (form) => ({ ...form, assigned_onboarding_form_ids: replace_assigned_onboarding_ids(assigned_ids) }),
      );
      if (this.selected_assign_verification_form
        && String(this.get_manual_verification_form_id(this.selected_assign_verification_form)) === String(manual_verification_form_id)) {
        this.selected_assign_verification_form = {
          ...this.selected_assign_verification_form,
          assigned_onboarding_form_ids: replace_assigned_onboarding_ids(assigned_ids),
        };
      }
    },

    get_first_assigned_onboarding_form(form) {
      const onboarding_form = get_first_assigned_onboarding_form_value(form, this.onboarding_forms);
      return onboarding_form;
    },
    handle_link_success(parsed, onboarding_form_id, verification_form_id) {
        this.update_form_assignment_state(verification_form_id, onboarding_form_id, true);
        this.show_success(parsed?.message || 'Manual verification form linked successfully.');
    },

    handle_link_failure(parsed) {
        this.show_error(parsed?.message || 'Failed to link manual verification form.');
    },
    handle_link_error(error, onboarding_form_id) {
        this.set_onboarding_link_state(onboarding_form_id, false);
        this.show_error(get_manual_verification_error_message(error, 'Failed to link manual verification form.'));
    },

    handle_unlink_success(parsed, onboarding_form_id, verification_form_id) {
        this.update_form_assignment_state(verification_form_id, onboarding_form_id, false);
        this.show_success(parsed?.message || 'Manual verification form unlinked successfully.');
    },
    handle_unlink_failure(parsed) {
        this.show_error(parsed?.message || 'Failed to unlink manual verification form.');
    },
    handle_unlink_error(error) {
        this.set_onboarding_link_state(this.linking_onboarding_form_id, true);
        this.show_error(get_manual_verification_error_message(error, 'Failed to unlink manual verification form.'));
    },

    async unlink_onboarding_form(onboarding_form_id, verification_form_id) {
        this.linking_onboarding_form_id = onboarding_form_id;
        this.linking_onboarding_form_action = 'unlink';

        try {
            const response = await axios.post(
            UNASSIGN_MANUAL_VERIFICATION_FORM_API,
            { onboarding_form_id, manual_verification_form_id: verification_form_id },
            get_request_config(),
            );

            const parsed = this.parse_response_data(response);
            const was_successful = parsed?.status === 'success';

            this.set_onboarding_link_state(onboarding_form_id, !was_successful);

            if (was_successful) {
            this.handle_unlink_success(parsed, onboarding_form_id, verification_form_id);
            } else {
            this.handle_unlink_failure(parsed);
            }

        } catch (error) {
            this.handle_unlink_error(error);

        } finally {
            this.linking_onboarding_form_id = null;
            this.linking_onboarding_form_action = null;
        }
        },

    async link_onboarding_form(onboarding_form_id, verification_form_id) {
        this.linking_onboarding_form_id = onboarding_form_id;
        this.linking_onboarding_form_action = 'link';

        try {
            const response = await axios.post(
            ASSIGN_MANUAL_VERIFICATION_FORM_API,
            { onboarding_form_id, manual_verification_form_id: verification_form_id },
            get_request_config(),
            );
            const parsed = this.parse_response_data(response);
            const was_successful = parsed?.status === 'success';
            this.set_onboarding_link_state(onboarding_form_id, was_successful);
            if (was_successful) {
            this.handle_link_success(parsed, onboarding_form_id, verification_form_id);
            } else {
            this.handle_link_failure(parsed);
            }
        } catch (error) {
            this.handle_link_error(error, onboarding_form_id);
        } finally {
            this.linking_onboarding_form_id = null;
            this.linking_onboarding_form_action = null;
        }
    },

    async on_toggle_link(onboarding_form, checked) {
      const onboarding_form_id = onboarding_form?.id;
      const verification_form_id = this.get_manual_verification_form_id(this.selected_assign_verification_form);
      if (!onboarding_form_id || !verification_form_id) {
        this.show_error('Missing onboarding or manual verification form id.');
        return;
      }
      if (checked) {
        await this.link_onboarding_form(onboarding_form_id, verification_form_id);
      } else {
        await this.unlink_onboarding_form(onboarding_form_id, verification_form_id);
      }
    },
    is_valid_manual_verification_form_id(id) {
        return Boolean(id);
    },
    get_active_link(linked_forms) {
        return (linked_forms || []).find((link) => link?.is_active !== false) || null;
    },
    resolve_active_onboarding_form(active_link, onboarding_forms, fallback_fn, form) {
        if (active_link) {
            return (onboarding_forms || []).find(
            (item) => String(item?.id) === String(active_link.onboarding_form_id)
            ) || null;
        }
        return fallback_fn(form);
    },
    async go_to_manual_verification_mapping(form) {
        const manual_verification_form_id = this.get_manual_verification_form_id(form);
        const manual_form_builder_id = this.first_non_null(
            form?.form_id,
            form?.survey_form_id,
            form?.survey_form,
        );
        if (!this.is_valid_manual_verification_form_id(manual_verification_form_id)) {
            this.show_error('Manual verification form id not found.');
            return;
        }
        if (!this.onboarding_forms.length) {
            await this.fetch_onboarding_forms();
        }
        const linked_forms = await this.fetch_linked_onboarding_forms(
            manual_verification_form_id,
            true,
        );
        const active_link = this.get_active_link(linked_forms);
        const active_onboarding_form = this.resolve_active_onboarding_form(
            active_link,
            this.onboarding_forms,
            this.get_first_assigned_onboarding_form,
            form,
        );
        const query = build_mapping_route_query(form, {
            manual_verification_form_id,
            manual_form_builder_id,
            active_link,
            active_onboarding_form,
        });
        this.$router.push({ name: 'ManualVerificationMapping', query });
    }
  },
};
